import React, { useEffect, useRef, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Select, { components as RsComponents } from 'react-select';
import { ucFirst } from '../../../helpers/generic';
import styles from '../../SelectComponents.module.css';


// Checkbox option with color bullet — selected items stay visible with a checkmark
const CheckboxColorOption = (props) => {
    return (
        <RsComponents.Option {...props}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                />
                {props.data.color && (
                    <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: props.data.color,
                        flexShrink: 0,
                    }} />
                )}
                {props.data.image && (
                    <img src={props.data.image} className={styles.image} alt="" />
                )}
                <div className={styles.infoContainer}>
                    {props.data.label}
                    {props.data.description && props.data.description !== '' && (
                        <span className={styles.description}>{props.data.description}</span>
                    )}
                </div>
            </div>
        </RsComponents.Option>
    );
};

const QuickFilterDropdown = ({ label, filterKey, options, value, onChange, isLoading }) => {
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const selectRef = useRef(null);

    useEffect(() => {
        if (show) {
            // Small timeout to let the display:block paint before focusing
            setTimeout(() => selectRef.current?.focus(), 10);
        }
    }, [show]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setShow(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const activeCount = Array.isArray(value) ? value.length : 0;

    const selectedValue = options.filter(o =>
        Array.isArray(value) && value.includes(o.value)
    );

    return (
        <div ref={ref} className="position-relative d-inline-block">
            <button
                className={`btn d-flex align-items-center gap-1 ${activeCount > 0 ? 'btn-primary' : 'btn-secondary'}${show ? ' show' : ''}`}
                style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                onClick={() => setShow(s => !s)}
                disabled={isLoading}
            >
                {label}
                {activeCount > 0 && (
                    <Badge bg="light" text="dark" style={{ fontSize: '11px' }}>{activeCount}</Badge>
                )}
                <span className="ms-1" style={{ display: 'inline-block', width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid currentColor', verticalAlign: 'middle' }} />
            </button>

            {show && <div
                className="position-absolute bg-white border rounded shadow-sm p-2"
                style={{ zIndex: 1050, top: '100%', left: 0, minWidth: '230px', marginTop: '4px' }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Select
                    ref={selectRef}
                    menuIsOpen
                    isMulti
                    isClearable={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={options}
                    value={selectedValue}
                    components={{ Option: CheckboxColorOption, IndicatorsContainer: () => null }}
                    placeholder={`Search ${label}...`}
                    onChange={(selected) => {
                        onChange(filterKey, selected ? selected.map(s => s.value) : []);
                    }}
                    classNamePrefix="select-component"
                    styles={{
                        control: (base) => ({ ...base, fontSize: '13px', minHeight: '32px', marginBottom: '4px' }),
                        menu: (base) => ({ ...base, position: 'static', boxShadow: 'none', border: 'none', margin: 0 }),
                        menuList: (base) => ({ ...base, maxHeight: '220px', padding: 0 }),
                        multiValue: () => ({ display: 'none' }),
                        option: (base) => ({ ...base, textAlign: 'left' }),
                        placeholder: (base) => ({ ...base, fontSize: '13px', color: '#999' }),
                    }}
                />
                {activeCount > 0 && (
                    <button
                        className="btn btn-sm btn-link text-danger p-0 mt-1"
                        style={{ fontSize: '12px' }}
                        onClick={() => onChange(filterKey, [])}
                    >
                        Clear all
                    </button>
                )}
            </div>}
        </div>
    );
};

const TicketsQuickFilters = ({ data, filters, onFilterChange, isLoading, isCustomer }) => {
    const buildOptions = (filterKey) => {
        const dataKeyMap = {
            status: 'statuses',
            department: 'departments',
            priority: 'priorities',
            assignee: 'users',
        };
        const rows = data[dataKeyMap[filterKey]] ?? [];
        return rows.map(row => ({
            value: filterKey === 'status' ? row.name : row.id,
            label: ucFirst(row.name),
            color: filterKey !== 'assignee' ? row.color : undefined,
            image: filterKey === 'assignee' ? row.profile_image : undefined,
            description: filterKey === 'assignee' ? row.email : undefined,
        }));
    };

    const handleChange = (filterKey, value) => {
        onFilterChange({ ...filters, [filterKey]: value });
    };

    const quickFilters = [
        { key: 'status', label: 'Status' },
        { key: 'department', label: 'Department' },
        { key: 'priority', label: 'Priority' },
        ...(!isCustomer ? [{ key: 'assignee', label: 'Assignee' }] : []),
    ];

    const hasAnyActive = quickFilters.some(({ key }) => (filters[key] ?? []).length > 0);

    const handleClearAll = () => {
        const cleared = quickFilters.reduce((acc, { key }) => ({ ...acc, [key]: [] }), {});
        onFilterChange({ ...filters, ...cleared });
    };

    return (
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
            {quickFilters.map(({ key, label }) => (
                <QuickFilterDropdown
                    key={key}
                    label={label}
                    filterKey={key}
                    options={buildOptions(key)}
                    value={filters[key] ?? []}
                    onChange={handleChange}
                    isLoading={isLoading}
                />
            ))}
            {hasAnyActive && (
                <button
                    className="btn btn-sm btn-link p-0 quick-filter-clear-btn"
                    style={{ fontSize: '14px', whiteSpace: 'nowrap', alignSelf: 'center' }}
                    onClick={handleClearAll}
                >
                    Clear All
                </button>
            )}
        </div>
    );
};

export default TicketsQuickFilters;
